import { useTheme } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import type { PortfolioReport } from '../types';
import {
  buildAccountBreakdownRows,
  buildGeneralInfoRows,
  HOLDING_COLUMNS,
  type PendingTradesState,
} from '../utils/portfolioRevampBuilders';
import { useEnrichedHoldings } from './useEnrichedHoldings';
import { useFormatMoney } from './useFormatMoney';
import { usePositionsPagination } from './usePositionsPagination';
import { useTradingTable } from './useTradingTable';
export const POSITIONS_PAGE_SIZE = 10;

export function usePortfolioRevampState(
  report: PortfolioReport,
  pendingTradesState: PendingTradesState,
  t: TFunction,
) {
  const theme = useTheme();
  const formatMoney = useFormatMoney();
  const enrichedHoldings = useEnrichedHoldings(report);

  const { table: holdingTable } = useTradingTable({
    data: enrichedHoldings,
    columns: HOLDING_COLUMNS,
    initialSorting: [{ id: 'value', desc: true }],
  });

  const generalInfoRows = useMemo(
    () =>
      buildGeneralInfoRows(
        report,
        enrichedHoldings,
        theme,
        pendingTradesState,
        t,
        formatMoney,
      ),
    [report, enrichedHoldings, theme, pendingTradesState, t, formatMoney],
  );

  const accountBreakdownRows = useMemo(
    () => buildAccountBreakdownRows(report),
    [report],
  );

  const {
    pagedRows: pagedPositionRows,
    page: positionsPage,
    setPage: setPositionsPage,
    totalPages: positionsTotalPages,
  } = usePositionsPagination(
    holdingTable.getRowModel().rows,
    POSITIONS_PAGE_SIZE,
  );

  return {
    generalInfoRows,
    accountBreakdownRows,
    holdingTable,
    pagedPositionRows,
    positionsPage,
    setPositionsPage,
    positionsTotalPages,
  };
}
